// src/App.jsx
import React from "react";
import { ApprovalNotificationProvider } from "./context/ApprovalNotificationContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { LeadProvider } from "./context/LeadContext";
import { AttendanceProvider } from "./context/AttendanceContext";
import { TaskProvider } from "./context/TaskContext";
import { MeetingProvider } from "./context/MeetingContext";
import { LeaveProvider } from "./context/LeaveContext";
import { NotesProvider } from "./context/NotesContext";
import { QueryProvider } from "./context/QueryContext";
import { NotificationProvider } from "./context/NotificationContext";
import ProtectedRoute, {
  getDashboardPath,
} from "./components/common/ProtectedRoute";
import { useAuth } from "./context/AuthContext";

import LoginPage from "./pages/auth/LoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminEmployees from "./pages/admin/AdminEmployees";
import AdminAttendance from "./pages/admin/AdminAttendance";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminLeave from "./pages/admin/AdminLeave";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminHolidays from "./pages/admin/AdminHolidays";
import AdminShifts from "./pages/admin/AdminShifts";
import { AdminMeetings } from "./pages/admin/AdminMeetings";
import AdminExtraBreaks from "./pages/admin/AdminExtraBreaks";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminAssignedCandidates from "./pages/admin/AdminAssignedCandidates";
import AdminHiringRequisitions from "./pages/admin/AdminHiringRequisitions";


import LeadDashboard from "./pages/lead/LeadDashboard";
import LeadMeetings from "./pages/lead/LeadMeetings";
import LeadTeam from "./pages/lead/LeadTeam";
import LeadTasks from "./pages/lead/LeadTasks";
import LeadShifts from "./pages/lead/LeadShifts";
import LeadQueries from "./pages/lead/LeadQueries";
import LeadNotes from "./pages/lead/LeadNotes";
import { LeadAttendance } from "./pages/lead/LeadAttendance";
import LeadAnnouncements from "./pages/lead/LeadAnnouncements";
import LeadLeave from "./pages/lead/LeadLeave";
import LeadAnalytics from "./pages/lead/LeadAnalytics";
import LeadExtraBreaks from "./pages/lead/LeadExtraBreaks";
import LeadRecruitment from "./pages/lead/LeadRecruitment";

import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import EmployeeTasks from "./pages/employee/EmployeeTasks";
import EmployeeLeave from "./pages/employee/EmployeeLeave";
import EmployeeNotes from "./pages/employee/EmployeeNotes";
import EmployeeQueries from "./pages/employee/EmployeeQueries";
import {
  EmployeeMeetings,
  EmployeeAnnouncements,
} from "./pages/employee/EmployeeMeetings";
import MyAttendance from "./pages/common/MyAttendance";

import TaskBoard from "./pages/TaskBoard";


import NotificationManager from "./components/notifications/NotificationManager";
import TravelReimbursement from "./pages/employee/TravelReimbursement";
import TravelReimbursementNew from "./pages/employee/TravelReimbursementNew";
import EmployeeProfile from "./pages/employee/EmployeeProfile";
import FinancePayrollDashboard from "./pages/finance/payroll/FinancePayrollDashboard";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import FinanceReimbursements from "./pages/finance/FinanceReimbursements";
import SalaryHistory from "./pages/finance/payroll/SalaryHistory";
import EmployeeJobBoard from "./pages/employee/EmployeeJobBoard";

// ── HR Module Pages ────────────────────────────────────────
import HRDashboard from "./pages/hr/HRDashboard";
import HRRecruitment from "./pages/hr/HRRecruitment";
import HROnboarding from "./pages/hr/HROnboarding";


import HRLearning from "./pages/hr/HRLearning";
import HRAssets from "./pages/hr/HRAssets";
import HREmployeeServices from "./pages/hr/HREmployeeServices";
import HROffboarding from "./pages/hr/HROffboarding";
import HRReports from "./pages/hr/HRReports";
import HRCompliance from "./pages/hr/HRCompliance";
import HRAdministration from "./pages/hr/HRAdministration";
import HRAIAssistant from "./pages/hr/HRAIAssistant";

import PayrollDashboard from "./pages/finance/payroll/PayrollDashboard";
import PayrollProcessing from "./pages/finance/payroll/PayrollProcessing";
import PayrollApproval from "./pages/finance/payroll/PayrollApproval";
import PayrollReports from "./pages/finance/payroll/PayrollReports";
import Payslips from "./pages/finance/payroll/Payslips";
import SalaryStructure from "./pages/finance/payroll/SalaryStructure";
import SalaryComponents from "./pages/finance/payroll/SalaryComponents";
import SalaryRegister from "./pages/finance/payroll/SalaryRegister";
import EmployeeSalaryAssignment from "./pages/finance/payroll/EmployeeSalaryAssignment";

function RootRedirect() {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Initialising…</p>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Loading your profile…</p>
      </div>
    );
  }
  // FIX: pass the full userProfile object (not userProfile.role) so
  // getDashboardPath -> getEffectiveRole can detect derived roles like
  // "finance" (an employee whose department is Finance).
  return <Navigate to={getDashboardPath(userProfile)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        
        <NotesProvider>
          <QueryProvider>
            <LeaveProvider>
               <ApprovalNotificationProvider>
              <AttendanceProvider>
                <TaskProvider>
                  <MeetingProvider>
                    <BrowserRouter>
                    <NotificationManager />
                      <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<LoginPage />} />

                        {/* ── Admin ──────────────────────────────── */}
                        <Route
                          path="/admin/dashboard"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/employees"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminEmployees />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/attendance"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/my-attendance"
                          element={
                            <ProtectedRoute role="admin">
                              <MyAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/tasks"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminTasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/taskboard"
                          element={
                            <ProtectedRoute role="admin">
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/meetings"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminMeetings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/announcements"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAnnouncements />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/leave"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminLeave />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/holidays"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminHolidays />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/shifts"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminShifts />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/reports"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAnalytics />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/settings"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/extra-breaks"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminExtraBreaks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/approvals"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminApprovals />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/assigned-candidates"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAssignedCandidates />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/hiring-requisitions"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminHiringRequisitions />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/services"
                          element={
                            <ProtectedRoute role="admin">
                              <HREmployeeServices />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/offboarding"
                          element={
                            <ProtectedRoute role="admin">
                              <HROffboarding />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/learning"
                          element={
                            <ProtectedRoute role="admin">
                              <HRLearning />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/payroll"
                          element={
                            <ProtectedRoute role={["admin", "hr", "finance", "finance-lead"]}>
                              <FinancePayrollDashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── Lead ─────────────────────────────────── */}
                        <Route
                          path="/lead/*"
                          element={
                            <ProtectedRoute role="lead">
                              <LeadProvider>
                                <Routes>
                                  <Route
                                    path="dashboard"
                                    element={<LeadDashboard />}
                                  />
                                  <Route
                                    path="attendance"
                                    element={<LeadAttendance />}
                                  />
                                  <Route path="reports" element={<LeadAnalytics />} />
                                  <Route
                                    path="my-attendance"
                                    element={<MyAttendance />}
                                  />
                                  <Route path="team" element={<LeadTeam />} />
                                  <Route path="tasks" element={<LeadTasks />} />
                                  <Route path="taskboard" element={<TaskBoard />} />
                                  <Route path="meetings" element={<LeadMeetings />} />
                                  <Route path="leave" element={<LeadLeave />} />
                                  <Route path="shifts" element={<LeadShifts />} />
                                  <Route
                                    path="announcements"
                                    element={<LeadAnnouncements />}
                                  />
                                  <Route
                                    path="services"
                                    element={<HREmployeeServices />}
                                  />
                                  <Route
                                    path="extra-breaks"
                                    element={<LeadExtraBreaks />}
                                  />
                                  <Route
                                    path="queries"
                                    element={<LeadQueries />}
                                  />
                                  <Route
                                    path="notes"
                                    element={<LeadNotes />}
                                  />
                                  <Route
                                    path="recruitment"
                                    element={<LeadRecruitment />}
                                  />
                                  <Route
                                    path="assigned-candidates"
                                    element={<LeadRecruitment />}
                                  />
                                  <Route
                                    path="offboarding"
                                    element={<HROffboarding />}
                                  />

                                  <Route
                                    path="learning"
                                    element={<HRLearning />}
                                  />
                                </Routes>
                              </LeadProvider>
                            </ProtectedRoute>
                          }
                        />

                        {/* ── Employee ─────────────────────────────── */}
                        <Route
                          path="/employee/dashboard"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/my-attendance"
                          element={
                            <ProtectedRoute>
                              <MyAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/tasks"
                          element={
                            <ProtectedRoute>
                              <EmployeeTasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/taskboard"
                          element={
                            <ProtectedRoute>
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/task-board"
                          element={
                            <ProtectedRoute>
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/taskboard"
                          element={
                            <ProtectedRoute>
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/meetings"
                          element={
                            <ProtectedRoute>
                              <EmployeeMeetings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/leave"
                          element={
                            <ProtectedRoute>
                              <EmployeeLeave />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/announcements"
                          element={
                            <ProtectedRoute>
                              <EmployeeAnnouncements />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/notes"
                          element={
                            <ProtectedRoute>
                              <EmployeeNotes />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/queries"
                          element={
                            <ProtectedRoute>
                              <EmployeeQueries />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/jobs"
                          element={
                            <ProtectedRoute>
                              <EmployeeJobBoard />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/employee/learning"
                          element={
                            <ProtectedRoute role={["employee", "lead", "admin", "hr"]}>
                              <HRLearning />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/profile"
                          element={
                            <ProtectedRoute>
                              <EmployeeProfile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/services"
                          element={
                            <ProtectedRoute role={["employee", "lead", "admin", "hr"]}>
                              <HREmployeeServices />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/offboarding"
                          element={
                            <ProtectedRoute role={["employee", "lead", "admin", "hr"]}>
                              <HROffboarding />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── HR Module ─────────────────────────────── */}
                        <Route
                          path="/hr/*"
                          element={
                            <ProtectedRoute role={["hr", "admin"]}>
                              <Routes>
                                {/* MAIN */}
                                <Route path="dashboard" element={<HRDashboard />} />
                                <Route path="employees" element={<AdminEmployees />} />
                                <Route path="attendance" element={<AdminAttendance />} />
                                <Route path="my-attendance" element={<MyAttendance />} />
                                <Route path="leave" element={<AdminLeave />} />
                                <Route path="approvals" element={<AdminApprovals />} />
                                <Route path="tasks" element={<AdminTasks />} />
                                <Route path="taskboard" element={<TaskBoard />} />
                                <Route path="meetings" element={<AdminMeetings />} />
                                <Route path="travel" element={<TravelReimbursement />} />
                                <Route path="announcements" element={<AdminAnnouncements />} />
                                <Route path="holidays" element={<AdminHolidays />} />
                                <Route path="shifts" element={<AdminShifts />} />

                                {/* CORE HR */}
                                <Route path="recruitment" element={<HRRecruitment />} />
                                <Route path="onboarding" element={<HROnboarding />} />

                                <Route path="learning" element={<HRLearning />} />

                                {/* OPERATIONS & SERVICES */}
                                <Route path="assets" element={<HRAssets />} />
                                <Route path="services" element={<HREmployeeServices />} />
                                <Route path="offboarding" element={<HROffboarding />} />

                                {/* REPORTS */}
                                <Route path="reports" element={<HRReports />} />

                                {/* ADMINISTRATION */}
                                <Route path="administration" element={<HRAdministration />} />
                                <Route path="settings" element={<AdminSettings />} />
                                <Route path="compliance" element={<HRCompliance />} />
                                <Route path="ai-assistant" element={<HRAIAssistant />} />
                              </Routes>
                            </ProtectedRoute>
                          }
                        />


                        <Route
                          path="/travel-reimbursement"
                          element={
                            <ProtectedRoute>
                              <TravelReimbursement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/travel-reimbursement/new"
                          element={
                            <ProtectedRoute>
                              <TravelReimbursementNew />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── Finance Module ────────────────────────── */}
                        <Route
                          path="/finance/*"
                          element={
                            <ProtectedRoute role={["admin", "hr", "finance", "finance-lead"]}>
                              <Routes>
                                <Route path="dashboard" element={<FinanceDashboard />} />
                                <Route path="payroll" element={<FinancePayrollDashboard />} />
                                <Route path="payroll/dashboard" element={<PayrollDashboard />} />
                                <Route path="payroll/processing" element={<PayrollProcessing />} />
                                <Route path="payroll/approval" element={<PayrollApproval />} />
                                <Route path="payroll/reports" element={<PayrollReports />} />
                                <Route path="payroll/payslips" element={<Payslips />} />
                                <Route path="payroll/salary-structure" element={<SalaryStructure />} />
                                <Route path="payroll/salary-components" element={<SalaryComponents />} />
                                <Route path="payroll/salary-register" element={<SalaryRegister />} />
                                <Route path="payroll/assign-salary" element={<EmployeeSalaryAssignment />} />
                                <Route path="reimbursements" element={<FinanceReimbursements />} />
                                <Route path="salary-history" element={<SalaryHistory />} />
                                <Route path="my-attendance" element={<MyAttendance />} />
                              </Routes>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/my-attendance"
                          element={
                            <ProtectedRoute>
                              <MyAttendance />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── Catch-all fallback for unmapped routes ──────────── */}
                        <Route
                          path="*"
                          element={<Navigate to="/login" replace />}
                        />
                      </Routes>
                    </BrowserRouter>
                  </MeetingProvider>
                </TaskProvider>
              </AttendanceProvider>
              </ApprovalNotificationProvider>
            </LeaveProvider>
          </QueryProvider>
        </NotesProvider>
         
      </NotificationProvider>
    </AuthProvider>
  );
}